import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getResponsiveGameConfig,
  generateSolvableCards,
  updateSelectableState,
  findMatchableCards,
  removeMatchedCards,
  canGameContinue,
  getGameHint,
  calculateScore
} from './gameLogic';
import './index.scss';

const SheepGame = () => {
  const [gameState, setGameState] = useState({
    cards: [],
    selectedCards: [],
    score: 0,
    level: 1,
    gameStatus: 'playing', // 'playing', 'won', 'lost'
    moves: 0
  });

  const [showRules, setShowRules] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [gameConfig, setGameConfig] = useState(getResponsiveGameConfig());

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setGameConfig(getResponsiveGameConfig());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 添加事件监听器
  useEffect(() => {
    const handleGameControl = (event) => {
      const { action, gameType } = event.detail;
      if (gameType !== 'sheep') return;
      
      switch (action) {
        case 'restart':
          resetGame();
          break;
        default:
          break;
      }
    };

    window.addEventListener('gameControl', handleGameControl);
    return () => window.removeEventListener('gameControl', handleGameControl);
  }, []);

  // 初始化游戏
  const initializeGame = useCallback(() => {
    const cards = generateSolvableCards();
    updateSelectableState(cards);

    setGameState({
      cards,
      selectedCards: [],
      score: 0,
      level: 1,
      gameStatus: 'playing',
      moves: 0
    });
  }, []);

  // 处理卡片点击
  const handleCardClick = useCallback((card) => {
    if (!card.selectable || card.selected || gameState.gameStatus !== 'playing') {
      return;
    }

    if (gameState.selectedCards.length >= gameConfig.MAX_SELECTED) {
      return;
    }

    setGameState(prevState => {
      const newSelectedCards = [...prevState.selectedCards, card];
      const newCards = prevState.cards.map(c => 
        c.id === card.id ? { ...c, selected: true, selectable: false } : c
      );

      // 检查是否有可消除的卡片
      const matchableTypes = findMatchableCards(newSelectedCards);
      let updatedSelectedCards = newSelectedCards;
      let newScore = prevState.score;
      let newMoves = prevState.moves + 1;

      // 消除匹配的卡片
      if (matchableTypes.length > 0) {
        updatedSelectedCards = removeMatchedCards(newSelectedCards, matchableTypes);
        newScore = calculateScore(matchableTypes, prevState.score, prevState.level);
      }

      // 更新可选择状态
      updateSelectableState(newCards);

      // 检查游戏状态
      const gameStatus = canGameContinue(newCards, updatedSelectedCards);
      let newGameStatus = prevState.gameStatus;
      
      if (!gameStatus.canContinue) {
        newGameStatus = gameStatus.reason === 'won' ? 'won' : 'lost';
      }

      return {
        ...prevState,
        cards: newCards,
        selectedCards: updatedSelectedCards,
        score: newScore,
        gameStatus: newGameStatus,
        moves: newMoves
      };
    });
  }, [gameState.gameStatus, gameState.selectedCards.length, gameConfig.MAX_SELECTED]);

  // 重置游戏
  const resetGame = useCallback(() => {
    initializeGame();
  }, [initializeGame]);

  // 撤销操作
  const undoMove = useCallback(() => {
    if (gameState.selectedCards.length === 0) return;

    setGameState(prevState => {
      const lastCard = prevState.selectedCards[prevState.selectedCards.length - 1];
      const newCards = prevState.cards.map(c => 
        c.id === lastCard.id ? { ...c, selected: false } : c
      );
      
      updateSelectableState(newCards);

      return {
        ...prevState,
        cards: newCards,
        selectedCards: prevState.selectedCards.slice(0, -1),
        gameStatus: 'playing'
      };
    });
  }, [gameState.selectedCards.length]);

  // 获取提示
  const getHint = useCallback(() => {
    const hint = getGameHint(gameState.cards, gameState.selectedCards);
    setShowHint(true);
    setTimeout(() => setShowHint(false), 3000);
    return hint;
  }, [gameState.cards, gameState.selectedCards]);

  // 初始化游戏
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // 渲染卡片
  const renderCard = (card) => {
    const style = {
      '--x': card.position.x,
      '--y': card.position.y,
      '--z': card.position.z,
      '--card-size': gameConfig.CARD_SIZE + 'px',
      '--card-spacing': gameConfig.CARD_SPACING + 'px',
      '--board-width': gameConfig.BOARD_WIDTH,
      '--board-height': gameConfig.BOARD_HEIGHT
    };

    return (
      <div
        key={card.id}
        className={`sheep-card ${card.selectable ? 'selectable' : ''} ${card.selected ? 'selected' : ''}`}
        style={style}
        onClick={() => handleCardClick(card)}
      >
        {card.type}
      </div>
    );
  };

  return (
    <div className="sheep-game">
      <div className="game-header">
        <div className="game-stats">
          <div className="stat">
            <span className="label">分数</span>
            <span className="value">{gameState.score}</span>
          </div>
          <div className="stat">
            <span className="label">步数</span>
            <span className="value">{gameState.moves}</span>
          </div>
          <div className="stat">
            <span className="label">选择</span>
            <span className="value">{gameState.selectedCards.length}/{gameConfig.MAX_SELECTED}</span>
          </div>
        </div>
        
        <div className="game-controls">
          <button 
            className="btn btn-primary" 
            onClick={resetGame}
          >
            重新开始
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={undoMove}
            disabled={gameState.selectedCards.length === 0}
          >
            撤销
          </button>
          <button 
            className="btn btn-warning" 
            onClick={getHint}
            disabled={gameState.gameStatus !== 'playing'}
          >
            提示
          </button>
          <button 
            className="btn btn-info" 
            onClick={() => setShowRules(!showRules)}
          >
            游戏规则
          </button>
        </div>
      </div>

      {showHint && (
        <div className="game-hint">
          <div className="hint-content">
            💡 {getGameHint(gameState.cards, gameState.selectedCards).message}
          </div>
        </div>
      )}

      {showRules && (
        <div className="game-rules">
          <h3>游戏规则</h3>
          <ul>
            <li>点击未被遮挡的卡片将其移入选择区域</li>
            <li>当选择区域有三个相同的卡片时自动消除</li>
            <li>选择区域最多可容纳{gameConfig.MAX_SELECTED}张卡片</li>
            <li>清空所有卡片即可获胜</li>
            <li>选择区域满{gameConfig.MAX_SELECTED}张且无法消除时游戏失败</li>
          </ul>
        </div>
      )}

      <div className="selected-cards">
        <h4>选择区域 ({gameState.selectedCards.length}/{gameConfig.MAX_SELECTED})</h4>
        <div className="selected-cards-container">
          {gameState.selectedCards.map((card, index) => (
            <div key={card.id} className="selected-card" style={{ '--index': index }}>
              {card.type}
            </div>
          ))}
        </div>
      </div>

      <div className="game-board">
        {gameState.cards.map(renderCard)}
      </div>

      {gameState.gameStatus === 'won' && (
        <div className="game-overlay">
          <div className="game-result">
            <h3>🎉 恭喜通关!</h3>
            <p>分数: {gameState.score}</p>
            <p>步数: {gameState.moves}</p>
            <button className="btn btn-primary" onClick={resetGame}>
              再玩一局
            </button>
          </div>
        </div>
      )}

      {gameState.gameStatus === 'lost' && (
        <div className="game-overlay">
          <div className="game-result">
            <h3>😢 游戏失败</h3>
            <p>选择区域已满，无法继续</p>
            <button className="btn btn-primary" onClick={resetGame}>
              重新挑战
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheepGame;
